import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationError } from 'webapp-types';
import { ApiMetricBuilderWizardStep } from 'webapp/enums/enums';

import CustomQuerySetupApiMetricBuilderRawResponseComponent from 'webapp/components/custom-query/setup/api-metric-builder/raw-response';
import type { Args as BaseArgs } from 'webapp/components/custom-query/setup/wizard/common/step-common';
import CustomQuerySetupWizardCommonStepCommonComponent from 'webapp/components/custom-query/setup/wizard/common/step-common';
import CustomQuerySetupWizardSheetControlsComponent from 'webapp/components/custom-query/setup/wizard/sheet/controls';
import FormErrorComponent from 'webapp/components/form-error';
import FormRadiobuttonComponent from 'webapp/components/form/radiobutton';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type { RowType as JSONStructureRowType } from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/json-structure/row';
import type JSONStructureRow from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/json-structure/row';

export interface Args extends BaseArgs {
	form: CustomMetricSetupFormApiMetricBuilder;
	stepId: JSONStructureRowType;
}

enum SelectedOption {
	SMTH = 'smth',
	NONE = '',
}

export default abstract class CustomQuerySetupWizardApiMetricBuilderStepCommonComponent extends CustomQuerySetupWizardCommonStepCommonComponent<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked selectedOption = SelectedOption.NONE;
	@tracked selectedRowIndex = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// if we are going by the "back" flow, we want to see the previously selected values
		if (this.args.form.jsonStructure?.hasRowForWizardStepBeenUpdated(this.args.stepId)) {
			// select what's already selected
			const selectedRow = this.args.form.jsonStructure?.getRowForWizardStep(this.args.stepId);
			if (selectedRow) {
				// already selected
				this.selectedOption = SelectedOption.SMTH;
				this.selectedRowIndex = selectedRow.index;
			}
		} else {
			this.selectFirstAvailable();
		}
	}

	get isNextDisabled(): boolean {
		return !this.isSelectionValid;
	}

	get isNoneShown(): boolean {
		return [ApiMetricBuilderWizardStep.DIMENSION, ApiMetricBuilderWizardStep.DATE].includes(
			this.args.stepId,
		);
	}

	get isNoneDisabled(): boolean {
		return false;
	}

	get noneDisabledText(): string {
		return '';
	}

	get isNoneSelected(): boolean {
		return this.selectedOption === SelectedOption.NONE;
	}

	get smthRadioLabel() {
		let text = `Use a field from response`;
		if (!this.isNoneDisabled) {
			text += ' (optional)';
		}
		return text;
	}

	get jsonStructureRow(): JSONStructureRow | null {
		const jsonStructure = this.args.form.jsonStructure;
		let row;
		if (jsonStructure) {
			row = jsonStructure.getRowForWizardStep(this.args.stepId);
		}
		return row || null;
	}

	get isSelectionValid(): boolean {
		return this.isNoneShown || !!this.jsonStructureRow;
	}

	get selectionWarnings(): ValidationError[] {
		const warnings: ValidationError[] = [],
			row = this.jsonStructureRow;
		if (row && !row.checkValidityForDataType(this.args.stepId)) {
			warnings.push({
				name: 'general',
				message: 'Row value might not match the type for this field.',
			});
		}
		return warnings;
	}

	selectRow(row: JSONStructureRow | null): void {
		const jsonStructure = this.args.form.jsonStructure;
		if (!jsonStructure) {
			return;
		}
		if (row && row.index !== this.selectedRowIndex) {
			this.selectedRowIndex = row.index;
		}
		jsonStructure.setRowForWizardStep(this.args.stepId, row);

		// reset and update extraction expression
		this.args.form.extractionExpression = '';
		this.args.form.updateExtractionExpression();
	}

	@action
	selectOption(value: AnySimpleValue): void {
		const option = value as SelectedOption;
		this.selectedOption = option;
		if (option) {
			this.selectFirstAvailable();
		} else {
			this.selectRow(null);
		}
	}

	@action
	onEditorRowSelect(rowIndex: number): void {
		const jsonStructure = this.args.form.jsonStructure;
		if (!jsonStructure) {
			return;
		}
		const row = jsonStructure.rows[rowIndex] || null;
		if (row && !row.isValid) {
			// this.infolayerService.error('Invalid row selected!');
			return;
		}
		this.selectRow(row);
	}

	@action
	goNext(): void {
		this.args.goNext();
	}

	private selectFirstAvailable(): void {
		const bestRow =
			this.args.form.jsonStructure?.getFirstAvailableRow(this.args.stepId) || null;

		if (bestRow) {
			this.selectedOption = SelectedOption.SMTH;
		} else {
			if (this.isNoneDisabled && this.isNoneSelected) {
				this.selectedOption = SelectedOption.SMTH;
			}
		}

		this.selectRow(bestRow);
	}
	<template>
		<div class="step-ct">

			<div class="top">
				<h3>{{this.stepTitle}}</h3>
				<p>
					{{{this.stepDescription}}}
				</p>

				{{yield}}
				{{! .toggle-manual button }}
			</div>

			<FormErrorComponent @errors={{this.selectionWarnings}} />

			{{#if this.isNoneShown}}
				<div class="radio radiobutton-default">
					<FormRadiobuttonComponent
						@name="field"
						@selected={{this.selectedOption}}
						@value=""
						@label={{this.optionNoneLabel}}
						@onChange={{this.selectOption}}
						@isDisabled={{this.isNoneDisabled}}
						@tooltipText={{this.noneDisabledText}}
					/>
				</div>

				<div class="radio radiobutton-default">
					<FormRadiobuttonComponent
						@name="field"
						@selected={{this.selectedOption}}
						@value="smth"
						@label={{this.smthRadioLabel}}
						@onChange={{this.selectOption}}
					/>
				</div>
			{{/if}}

			<CustomQuerySetupApiMetricBuilderRawResponseComponent
				@form={{@form}}
				@isLoading={{false}}
				@isDisabled={{this.isNoneSelected}}
				@selectedRowIndex={{this.selectedRowIndex}}
				@onRowSelect={{this.onEditorRowSelect}}
				@theme={{this.theme}}
			/>

		</div>

		{{! controlsNode could still be unrendered }}
		{{#if @controlsNode}}
			{{#in-element @controlsNode}}
				<CustomQuerySetupWizardSheetControlsComponent
					@showBack={{true}}
					@isButtonDisabled={{this.isNextDisabled}}
					@submitInProgress={{this.submitInProgress}}
					@goNext={{this.goNext}}
					@goPrevious={{@goPrevious}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
